import { Link, NavLink, Outlet } from 'react-router-dom';
import { LogOut, TrendingUp } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import clsx from 'clsx';

/**
 * Header-only layout — no sidebar. The portal is intentionally
 * shallow: a single dashboard plus the board-meeting record set.
 * Two nav tabs in the header is the entire navigation surface.
 */
function HeaderTab({
  to,
  end,
  children
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        clsx(
          'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
          isActive
            ? 'bg-white/[0.06] text-nurev-text'
            : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
        )
      }
    >
      {children}
    </NavLink>
  );
}

export function MainLayout() {
  const investor = useAuthStore(s => s.investor);
  const clear = useAuthStore(s => s.clear);

  return (
    <div className="min-h-screen bg-nurev-bg">
      <header className="sticky top-0 z-10 border-b border-nurev-border bg-nurev-bg/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700">
                <TrendingUp className="h-4 w-4 text-white" />
              </span>
              <div>
                <p className="text-sm font-bold leading-tight text-nurev-text">
                  NUREV
                </p>
                <p className="text-[10px] leading-tight text-nurev-muted">
                  Investor Portal
                </p>
              </div>
            </Link>

            <nav className="flex items-center gap-1">
              <HeaderTab to="/" end>
                Dashboard
              </HeaderTab>
              <HeaderTab to="/meetings">Meetings</HeaderTab>
              <HeaderTab to="/system-map">System Map</HeaderTab>
              <HeaderTab to="/revenue-model">Revenue Model</HeaderTab>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            {investor && (
              <div className="text-right">
                <p className="text-xs font-medium text-nurev-text">
                  {investor.name}
                </p>
                <p className="text-[10px] text-nurev-muted">{investor.email}</p>
              </div>
            )}
            <button
              onClick={() => clear()}
              title="Sign out"
              className="rounded-md p-1.5 text-nurev-muted transition hover:bg-white/5 hover:text-nurev-text"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-6xl px-6 py-6 text-center text-[10px] text-nurev-muted">
        NUREV.IO, Inc. · Confidential — Distribute only to authorized shareholders.
      </footer>
    </div>
  );
}
